import { useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast, TradingToast } from '@trading-ui/core';
import { useTranslation } from 'react-i18next';

import { getAllPendingActions, changeActionStatus } from '@/api/services/settings/moeve-actions.service';

import { parseBackendReason } from '@/utils/error.utils';

import { QUERY_KEYS } from '@/api/config/query-keys';

import type { ProcessedBackendError } from '@/interfaces';

export const usePendingActions = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  const { t } = useTranslation();

  const {
    data: pendingActions,
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey: [QUERY_KEYS.PENDING_ACTIONS],
    queryFn: getAllPendingActions,
  });

  useEffect(() => {
    if (error) {
      const errorMessage = parseBackendReason(error as Error | ProcessedBackendError);
      addToast(<TradingToast type="error" title="Error" message={errorMessage} />);
    }
  }, [error, addToast]);

  const handleChangeActionStatus = useCallback(
    async (actionId: number, statusId: number, role?: string, companyIds?: number[]) => {
      try {
        await changeActionStatus({ actionId, statusId, role, companyIds });

        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PENDING_ACTIONS] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_COMPANIES] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_DETAILS] });
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.BIOFLUX_MASTERS] });

        addToast(<TradingToast message={t('services.pendingActions.actionStatusChanged')} type="success" />);
      } catch (err) {
        const errorMessage = parseBackendReason(err as Error | ProcessedBackendError);
        addToast(<TradingToast title="Error" message={errorMessage} type="error" />);
      }
    },
    [queryClient, addToast, t]
  );

  return { pendingActions, isLoading, isRefetching, error, handleChangeActionStatus };
};
